import { type FilterType, type IDateFilter, type IMultiSelectFilter } from '@psyomics/ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetReferralsFilters } from './generated/referrals';
import { AllFilters, GlobalSearch } from './types';

const useReferredDateFilter = (): FilterType => {
  const { t } = useTranslation();
  const [fromReferredDateFilter, setFromReferredDateFilter] = useState<Date | null>(null);
  const [toReferredDateFilter, setToReferredDateFilter] = useState<Date | null>(null);

  return useMemo(
    () => ({
      id: 'referredDate',
      kind: 'date',
      name: t('model.referrals.referral-date'),
      onChange: {
        from: setFromReferredDateFilter,
        to: setToReferredDateFilter,
      },
      value: {
        from: fromReferredDateFilter,
        to: toReferredDateFilter,
      },
    }),
    [fromReferredDateFilter, setFromReferredDateFilter, toReferredDateFilter, setToReferredDateFilter, t]
  );
};

const useCompletedDateFilter = (): FilterType => {
  const { t } = useTranslation();
  const [fromCompletedDateFilter, setFromCompletedDateFilter] = useState<Date | null>(null);
  const [toCompletedDateFilter, setToCompletedDateFilter] = useState<Date | null>(null);

  return useMemo(
    () => ({
      id: 'completedDate',
      kind: 'date',
      name: t('model.referrals.completion-date'),
      onChange: {
        from: setFromCompletedDateFilter,
        to: setToCompletedDateFilter,
      },
      value: {
        from: fromCompletedDateFilter,
        to: toCompletedDateFilter,
      },
    }),
    [fromCompletedDateFilter, setFromCompletedDateFilter, toCompletedDateFilter, setToCompletedDateFilter, t]
  );
};

const useServiceFilter = (serviceCodes: string[]): FilterType => {
  const { t } = useTranslation();
  const [serviceFilter, setServiceFilter] = useState<string[] | null>(null);

  return useMemo(
    () => ({
      defaultValue: null,
      id: 'service',
      kind: 'multi-select',
      name: t('model.referrals.service'),
      onChange: setServiceFilter,
      value: serviceFilter,
      values: serviceCodes,
    }),
    [serviceCodes, serviceFilter, setServiceFilter, t]
  );
};

const useFilterState = (serviceCodes: string[]) => {
  const referredDate = useReferredDateFilter();
  const completedDate = useCompletedDateFilter();
  const service = useServiceFilter(serviceCodes);

  return useMemo(
    () => ({
      referredDate,
      completedDate,
      service,
    }),
    [referredDate, completedDate, service]
  );
};

const useResetPageWhenFiltersChange = (
  filters: FilterType[],
  globalSearch: string | null,
  resetPageIndex: () => void
) =>
  useEffect(() => {
    resetPageIndex();
  }, [filters, resetPageIndex, globalSearch]);

const useGlobalSearch = (): GlobalSearch => {
  const [value, setValue] = useState<string | null>(null);

  return {
    onChange: setValue,
    value,
  };
};

const useFilters = (resetPageIndex: () => void, serviceCodes: string[]): AllFilters => {
  const { referredDate, completedDate, service } = useFilterState(serviceCodes);
  const filters = useMemo(() => [referredDate, completedDate, service], [referredDate, completedDate, service]);
  const globalSearch = useGlobalSearch();
  useResetPageWhenFiltersChange(filters, globalSearch.value, resetPageIndex);

  return { filters, globalSearch };
};

type FilterMap = {
  referredDate: {
    filter: IDateFilter;
    mapTo: {
      from: 'fromReferralDate';
      to: 'toReferralDate';
    };
  };
  completedDate: {
    filter: IDateFilter;
    mapTo: {
      from: 'fromCompletedDate';
      to: 'toCompletedDate';
    };
  };
  service: {
    filter: IMultiSelectFilter;
    mapTo: 'serviceCodes';
  };
};

const getDateFilter = <K extends keyof FilterMap>(
  filters: FilterType[],
  filterId: K
): FilterMap[K]['filter'] | undefined => {
  return filters.find((filter): filter is FilterMap[K]['filter'] => filter.id === filterId && filter.kind === 'date');
};

const getServiceFilter = (filters: FilterType[]): IMultiSelectFilter | undefined => {
  return filters.find(
    (filter): filter is IMultiSelectFilter => filter.id === 'service' && filter.kind === 'multi-select'
  );
};

const useQueryFilters = (filters: FilterType[], showCompletedOnly?: boolean): GetReferralsFilters => {
  return useMemo(() => {
    const result: GetReferralsFilters = {};

    const referredDateFilter = getDateFilter(filters, 'referredDate');
    if (referredDateFilter) {
      result.fromReferralDate = referredDateFilter.value.from;
      result.toReferralDate = referredDateFilter.value.to;
    }
    const completedDateFilter = getDateFilter(filters, 'completedDate');
    if (completedDateFilter) {
      result.fromCompletedDate = completedDateFilter.value.from;
      result.toCompletedDate = completedDateFilter.value.to;
    }
    const serviceFilter = getServiceFilter(filters);
    if (serviceFilter?.value?.length) {
      result.serviceCodes = serviceFilter.value;
    }
    result.showCompletedOnly = showCompletedOnly;
    return result;
  }, [filters, showCompletedOnly]);
};

export { useFilters, useQueryFilters };
